// @flow

import React, { PureComponent } from "react";
import { get, noop, isNumber } from "lodash";
import { formatRaceDate } from "@tvg/formatter/dates";
import { formatPurse } from "@tvg/formatter/currency";
import type { PastPerformanceRace as PastPerformanceRaceType } from "@tvg/types/Race";
import type { Entry } from "@tvg/types/ListItem";
import type { NullaryFn } from "@tvg/types/Functional";

import PositionInfo from "../../_atom/PositionInfo";
import Icon from "../../_static/Icons";
import { arrowRight, hasReplay, noReplay } from "../../_static/Icons/icons";
import buildColor from "../../_static/ColorPalette";
import {
  PPCard,
  PPCardHeader,
  PPCardOptions,
  Date,
  RaceInfo,
  TitleWrapper,
  Title,
  TitleComplement,
  RaceDataList,
  RaceDataListItem,
  RaceReport,
  IconWrapper,
  FavoriteTag,
  Separator
} from "./styled-components";

type Props = {
  race: PastPerformanceRaceType,
  isSelected: boolean,
  onClick: NullaryFn<void>,
  raceInfoSeparator: string,
  postRaceReportShown: boolean,
  distanceHighlighted: boolean,
  surfaceHighlighted: boolean
};

const RUNNER_FINAL_ODD_KEY = "runnerFinalOdd";
const DISTANCE_KEY = "distance";
const SURFACE_KEY = "surface";
const CONDITION_KEY = "condition";
const CLASS_KEY = "class";
const PURSE_KEY = "purse";

export default class PastPerformance extends PureComponent<Props> {
  static defaultProps = {
    onClick: noop,
    isSelected: false,
    raceInfoSeparator: " | ",
    postRaceReportShown: true,
    distanceHighlighted: false,
    surfaceHighlighted: false
  };

  raceInfoItems: Entry[] = [
    {
      key: RUNNER_FINAL_ODD_KEY,
      value: this.getRunnerFinalOdd(),
      isHighlighted: false
    },
    {
      key: DISTANCE_KEY,
      value: get(this.props.race, "distance"),
      isHighlighted: this.props.distanceHighlighted
    },
    {
      key: SURFACE_KEY,
      value: get(this.props.race, "surface.shortName"),
      isHighlighted: this.props.surfaceHighlighted
    },
    {
      key: CONDITION_KEY,
      value: get(this.props.race, "surface.defaultCondition"),
      isHighlighted: false
    },
    {
      key: CLASS_KEY,
      value: get(this.props.race, "raceClass.shortName"),
      isHighlighted: false
    },
    {
      key: PURSE_KEY,
      value: formatPurse(get(this.props.race, "purse")),
      isHighlighted: false
    }
  ];

  getRunnerFinalOdd() {
    const { odds } = this.props.race;
    if (this.props.race.scratched || !odds || !isNumber(odds.numerator))
      return "";
    // $FlowFixMe
    return `(${odds.numerator}/${
      // $FlowFixMe
      isNumber(odds.denominator) ? odds.denominator : 1
    })`;
  }

  renderDate = () => (
    <Date data-qa-label="race-info-date">
      {formatRaceDate(this.props.race.raceDate || this.props.race.date)}
    </Date>
  );

  renderPosition = () => (
    <PositionInfo
      finishPosition={this.props.race.finishPosition}
      numRunners={this.props.race.numRunners}
      accBeatenDistance={{
        accBeatenDistance: get(this.props.race, "accBeatenDistance", ""),
        accBeatenDistanceStatus: get(
          this.props.race,
          "accBeatenDistanceStatus",
          ""
        )
      }}
    />
  );

  renderReplay = () => (
    <IconWrapper>
      {this.props.race.hasReplays ? (
        <Icon
          qaLabel="video-replay"
          color={buildColor("blue_accent", "500")}
          icon={hasReplay}
        />
      ) : (
        <Icon
          qaLabel="video-no-replay"
          color={buildColor("grey", "800")}
          icon={noReplay}
        />
      )}
    </IconWrapper>
  );

  renderResultNavigation = () => (
    <IconWrapper>
      <Icon
        qaLabel="result"
        color={buildColor("blue_accent", "500")}
        icon={arrowRight}
      />
    </IconWrapper>
  );

  renderInfoItem = (entry: Entry, hasSeparator: boolean, isFavorite: boolean) =>
    entry.value &&
    (isFavorite ? (
      <RaceDataListItem
        key={entry.key}
        data-qa-label={`race-info-${entry.key}`}
      >
        {entry.value}
        {isFavorite && <FavoriteTag>FAV</FavoriteTag>}
        {hasSeparator && <Separator>{this.props.raceInfoSeparator}</Separator>}
      </RaceDataListItem>
    ) : (
      <RaceDataListItem
        key={entry.key}
        data-qa-label={`race-info-${entry.key}`}
        isHighlighted={entry.isHighlighted && this.props.postRaceReportShown}
      >
        {entry.value}
        {hasSeparator && <Separator>{this.props.raceInfoSeparator}</Separator>}
      </RaceDataListItem>
    ));

  renderInfo = () => {
    const { favorite } = this.props.race;
    return (
      <RaceInfo>
        <TitleWrapper>
          <Title data-qa-label="race-track-name">
            {this.props.race.track.name}
          </Title>
          <TitleComplement data-qa-label="race-number">
            &nbsp;R{this.props.race.number}
          </TitleComplement>
        </TitleWrapper>
        <RaceDataList>
          {this.raceInfoItems.map((infoItem, infoIndex) =>
            this.renderInfoItem(
              infoItem,
              infoIndex < this.raceInfoItems.length - 1,
              infoIndex === 0 && !!favorite
            )
          )}
        </RaceDataList>
        {this.props.postRaceReportShown && this.props.race.postRaceReport && (
          <RaceReport>{`"${this.props.race.postRaceReport.replace(
            /,(?=[^\s])/g,
            " | "
          )}"`}</RaceReport>
        )}
      </RaceInfo>
    );
  };

  render() {
    return (
      <PPCard
        onClick={!this.props.isSelected ? this.props.onClick : noop}
        isSelected={this.props.isSelected}
      >
        <PPCardHeader>
          {this.renderPosition()}
          <PPCardOptions>
            {this.renderDate()}
            {this.renderReplay()}
            {this.renderResultNavigation()}
          </PPCardOptions>
        </PPCardHeader>
        {this.renderInfo()}
      </PPCard>
    );
  }
}
